import React, { useMemo, useState } from "react";
import { last } from "ramda";
import PropTypes from "prop-types";
import isTouchDevice from "is-touch-device";
import pluralize from "pluralize";
import { Button, Link, Typography } from "@mui/material";
import { NavLink } from "react-router-dom";
import clsx from "clsx";

import { useScreen, useTemplateGroupTracking } from "hooks";
import ResponsiveImage from "components/ResponsiveImage";
import { handlerSelectItemEvent } from "tracking/google/analytics";
import { useCategory, useHomePage } from "hooks";
import Placeholder from "components/Placeholder";
import StarringYouPresenter from "presenters/StarringYouPresenter";
import StarringYouClipPresenter from "presenters/StarringYouClipPresenter";
import LazyLoad from "components/LazyLoad";

import useStyles from "./useStyles";
import Details from "./components/Details/Details";

const STARRING_YOU_WIDTH = 400;

const StarringYou = ({
  item,
  lazy,
  loading = false,
  overflow = false,
  isClip = false,
  showDetails = false,
  onClick,
  hasModulePosition = false,
}) => {
  const classes = useStyles();
  const { isMobile } = useScreen();
  const { trackClick } = useTemplateGroupTracking(item);
  const { category } = useCategory();
  const { homePage } = useHomePage();
  const [overlayVisible, setOverlayVisible] = useState(false);
  const thumbnail = isClip ? StarringYouClipPresenter.previewPoster(item) : StarringYouPresenter.thumb(item);
  const castOptions = isClip
    ? StarringYouClipPresenter.castNumOptions(item)
    : StarringYouPresenter.castNumOptions(item);

  const STARRING_YOU_HEIGHT = useMemo(() => {
    if (!item) return undefined;

    return StarringYouPresenter.heightFromAspectRatio(item, STARRING_YOU_WIDTH);
  }, [item]);

  const buildName = () => {
    const baseName = StarringYouPresenter.name(item);
    return StarringYouPresenter.isInvite(item) ? `${baseName} Invitation` : baseName;
  };

  const handleMouseEnter = (e) => {
    if (isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setOverlayVisible(true);
  };

  const handleMouseLeave = () => {
    setOverlayVisible(false);
  };

  const handleClick = (e) => {
    if (isTouchDevice() && !overlayVisible) {
      e.preventDefault();
      e.stopPropagation();
      setOverlayVisible(true);
      return false;
    }

    if (onClick) onClick(item);

    if (!hasModulePosition) handlerSelectItemEvent(item, category, homePage);
    return trackClick();
  };

  const renderCastOptions = (castNumOptions) => {
    const max = last(castNumOptions);
    const min = castNumOptions[0];

    if (max !== min) {
      return (
        <>
          {min} to {max} <br /> People
        </>
      );
    }

    return (
      <>
        {min} <br /> {pluralize("Person", min)}
      </>
    );
  };

  if (loading)
    return (
      <div data-testid="starringyou-placeholder" className={classes.card}>
        <Placeholder variant="rect" ratio="4/6" />
      </div>
    );

  return (
    <>
      <Link to={StarringYouPresenter.handleRedirect(item)} onClick={handleClick} component={NavLink}>
        <div
          role="presentation"
          className={classes.card}
          onMouseEnter={handleMouseEnter}
          onFocus={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onBlur={handleMouseLeave}
        >
          {thumbnail && (
            <LazyLoad lazy={lazy} overflow={overflow}>
              <ResponsiveImage
                className={classes.cardImg}
                src={thumbnail}
                alt={buildName()}
                width={STARRING_YOU_WIDTH}
                height={STARRING_YOU_HEIGHT}
                widthMobile={226}
              />
            </LazyLoad>
          )}
          {overlayVisible && (
            <div className={classes.overlay}>
              <Typography
                data-testid="starringyou-castoptions"
                variant="h3"
                fontWeight={600}
                align="center"
                color="neutral.main"
              >
                {renderCastOptions(castOptions)}
              </Typography>
              <Button
                className={clsx(classes.overlayBtn, {
                  [classes.smallOverlayBtnClip]: isClip,
                })}
                variant="contained"
                size={isMobile ? "small" : "medium"}
                color="primary"
              >
                GET STARTED
              </Button>
            </div>
          )}
        </div>
      </Link>
      {showDetails ? <Details name={buildName()} castOptions={castOptions} /> : null}
    </>
  );
};

StarringYou.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    attributes: PropTypes.shape({}),
  }),
  lazy: PropTypes.bool,
  loading: PropTypes.bool,
  overflow: PropTypes.bool,
  isClip: PropTypes.bool,
  showDetails: PropTypes.bool,
  onClick: PropTypes.func,
  hasModulePosition: PropTypes.bool,
};

export default StarringYou;
